import React, { useState, useEffect } from 'react';
import { 
  Users, 
  DollarSign, 
  RefreshCw,
  LogOut,
  BarChart3,
  Settings
} from 'lucide-react';
import Logo from './Logo';
import './InfluencerDashboard.css';
import { apiRequest } from '../config/api';

export default function InfluencerDashboard() {
  const [influencer, setInfluencer] = useState(null);
  const [metrics, setMetrics] = useState({
    totalReferrals: 0,
    recentReferrals: 0,
    totalEarnings: 0,
    couponUses: 0
  });
  const [recentReferrals, setRecentReferrals] = useState([]);
  const [affiliateLink, setAffiliateLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Cargar datos reales del influencer
  useEffect(() => {
    const loadInfluencerData = async () => {
      try {
        // Verificar si hay sesión de influencer
        const influencerSession = localStorage.getItem('influencerSession');
        if (!influencerSession) {
          // Redirigir al login si no hay sesión
          window.location.href = '/influencer/login';
          return;
        }

        const influencerData = JSON.parse(influencerSession);
        setInfluencer(influencerData);
        setAffiliateLink(`${window.location.origin}/register?ref=${influencerData.code}`);

        // Cargar métricas reales del backend
        try {
          const data = await apiRequest('/api/influencers/dashboard', {
            method: 'POST',
            body: {
              influencerCode: influencerData.code
            }
          });

          if (data.success) {
            setMetrics(data.metrics);
            setRecentReferrals(data.recentReferrals || []);
          }
        } catch (apiError) {
          console.error('Error cargando datos del dashboard:', apiError);
          // Usar datos mock como fallback
          setMetrics({
            totalReferrals: 0,
            recentReferrals: 0,
            totalEarnings: 0,
            couponUses: 0
          });
        }
      } catch (error) {
        console.error('Error cargando datos del influencer:', error);
        // Usar datos mock como fallback
        setMetrics({
          totalReferrals: 0,
          recentReferrals: 0,
          totalEarnings: 0,
          couponUses: 0
        });
      } finally {
        setLoading(false);
      }
    };

    loadInfluencerData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const influencerSession = localStorage.getItem('influencerSession');
      if (influencerSession) {
        const influencerData = JSON.parse(influencerSession);
        
        // Recargar métricas reales del backend
        const response = await fetch('https://worker-production-86fa.up.railway.app/api/influencers/dashboard', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            influencerCode: influencerData.code
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setMetrics(data.metrics);
            setRecentReferrals(data.recentReferrals || []);
          }
        }
      }
    } catch (error) {
      console.error('Error actualizando datos:', error);
    } finally {
      setRefreshing(false);
    }
  };


  const copyAffiliateLink = () => {
    navigator.clipboard.writeText(affiliateLink);
    // Mostrar toast de éxito
    alert('¡Enlace copiado al portapapeles!');
  };

  const handleLogout = () => {
    localStorage.removeItem('influencerSession');
    window.location.href = '/influencer/login';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    try {
      // Si es un string, convertir a Date
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      
      // Verificar que la fecha es válida
      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date received:', date);
        return 'Fecha inválida';
      }
      
      return new Intl.DateTimeFormat('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(dateObj);
    } catch (error) {
      console.error('Error formatting date:', error, 'Date:', date);
      return 'Error de fecha';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="influencer-dashboard">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="influencer-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <Logo size="header-logo" className="header-logo" />
            <p>Panel de Influencer</p>
          </div>
          <div className="header-right">
            <div className="user-info">
              <span>Hola, {influencer?.name || 'Influencer'}</span>
              <span className="plan-badge">Influencer</span>
            </div>
            <button 
              className="btn btn-secondary refresh-btn"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={refreshing ? 'spinning' : ''} size={18} />
              Actualizar
            </button>
            <button onClick={handleLogout} className="btn btn-secondary logout-btn">
              <LogOut size={18} />
              Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="dashboard-nav">
        <div className="nav-content">
          <button 
            className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <BarChart3 size={20} />
            Resumen
          </button>
          <button 
            className={`nav-tab ${activeTab === 'referrals' ? 'active' : ''}`}
            onClick={() => setActiveTab('referrals')}
          >
            <Users size={20} />
            Referidos
          </button>
          <button 
            className={`nav-tab ${activeTab === 'earnings' ? 'active' : ''}`}
            onClick={() => setActiveTab('earnings')}
          >
            <DollarSign size={20} />
            Ganancias
          </button>
          <button 
            className={`nav-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={20} />
            Configuración
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="dashboard-main">
        <div className="main-content">
          {activeTab === 'overview' && (
            <OverviewTab 
              metrics={metrics}
              affiliateLink={affiliateLink}
              onCopyLink={copyAffiliateLink}
              influencer={influencer}
            />
          )}
          {activeTab === 'referrals' && (
            <ReferralsTab 
              recentReferrals={recentReferrals}
              formatDate={formatDate}
            />
          )}
          {activeTab === 'earnings' && (
            <EarningsTab 
              metrics={metrics}
              formatCurrency={formatCurrency}
            />
          )}
          {activeTab === 'settings' && (
            <SettingsTab 
              influencer={influencer}
            />
          )}
        </div>
      </main>

    </div>
  );
}

// Componente de Resumen
function OverviewTab({ metrics, affiliateLink, onCopyLink, influencer }) {
  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>Resumen General</h2>
        <p>Vista general de tu actividad como influencer</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>Total Referidos</h3>
            <p className="stat-value">{metrics.totalReferrals}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">⏰</div>
          <div className="stat-content">
            <h3>Últimas 24h</h3>
            <p className="stat-value">{metrics.recentReferrals}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>Ganancias Totales</h3>
            <p className="stat-value">${metrics.totalEarnings.toLocaleString('es-MX')} MXN</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🎁</div>
          <div className="stat-content">
            <h3>Cupones Usados</h3>
            <p className="stat-value">{metrics.couponUses}</p>
          </div>
        </div>
      </div>

      {/* Enlace de afiliado - COMENTADO TEMPORALMENTE */}
      {/* 
      <div className="affiliate-section">
        <h3>Enlace de Afiliado</h3>
        <div className="affiliate-link-container">
          <input 
            type="text" 
            value={affiliateLink} 
            readOnly 
            className="affiliate-input"
          />
          <button 
            className="btn btn-primary copy-btn"
            onClick={onCopyLink}
          >
            <Copy size={18} />
            Copiar
          </button>
          <button 
            className="btn btn-secondary external-btn"
            onClick={() => window.open(affiliateLink, '_blank')}
          >
            <ExternalLink size={18} />
            Abrir
          </button>
        </div>
        <p className="affiliate-info">
          Comparte este enlace para que los usuarios se registren con tu código: <strong>{influencer?.code}</strong>
        </p>
      </div>
      */}
    </div>
  );
}

// Componente de Referidos
function ReferralsTab({ recentReferrals, formatDate }) {
  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>Referidos Recientes</h2>
        <p>Lista de usuarios que completaron el pago con tu código</p>
      </div>

      <div className="referrals-section">
        {recentReferrals.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h3>No hay referidos aún</h3>
            <p>Los usuarios que completen el pago con tu código aparecerán aquí</p>
          </div>
        ) : (
          <div className="referrals-table">
            <div className="table-header">
              <div>Usuario</div>
              <div>Email</div>
              <div>Plan</div>
              <div>Fecha de Pago</div>
              <div>Comisión</div>
              <div>Estado</div>
            </div>
            {recentReferrals.map((referral) => (
              <div key={referral.id} className="table-row">
                <div className="user-info">
                  <div className="username">{referral.username}</div>
                </div>
                <div className="email">{referral.userEmail}</div>
                <div className="plan">
                  <span className={`plan-badge ${referral.plan}`}>
                    {referral.plan === 'basico' ? 'Básico' : 
                     referral.plan === 'premium' ? 'Premium' : 
                     referral.plan === 'elite' ? 'Élite' : referral.plan}
                  </span>
                </div>
                <div className="date">{formatDate(referral.paidAt || referral.createdAt)}</div>
        <div className="commission">
          <span className={`commission-badge ${referral.commissionStatus || 'pending'}`}>
            ${(referral.commissionAmount || 0).toLocaleString('es-MX')} MXN
          </span>
        </div>
                <div className="status">
                  <span className={`status-badge ${referral.status}`}>
                    {referral.status === 'paid' ? 'Pagado' : referral.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Componente de Ganancias
function EarningsTab({ metrics, formatCurrency }) {
  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>Ganancias y Comisiones</h2>
        <p>Resumen de tus ganancias como influencer</p>
      </div>

      <div className="earnings-section">
        <div className="earnings-grid">
          <div className="earnings-card">
            <div className="earnings-icon">💰</div>
            <div className="earnings-content">
              <h3>Ganancias Totales</h3>
              <p className="earnings-value">{formatCurrency(metrics.totalEarnings)}</p>
            </div>
          </div>

          <div className="earnings-card">
            <div className="earnings-icon">📊</div>
            <div className="earnings-content">
              <h3>Referidos Totales</h3>
              <p className="earnings-value">{metrics.totalReferrals}</p>
            </div>
          </div>

          <div className="earnings-card">
            <div className="earnings-icon">🎁</div>
            <div className="earnings-content">
              <h3>Cupones Usados</h3>
              <p className="earnings-value">{metrics.couponUses}</p>
            </div>
          </div>

          <div className="earnings-card">
            <div className="earnings-icon">⏰</div>
            <div className="earnings-content">
              <h3>Últimas 24h</h3>
              <p className="earnings-value">{metrics.recentReferrals}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Componente de Configuración
function SettingsTab({ influencer }) {
  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>Configuración de Cuenta</h2>
        <p>Gestiona tu información de influencer</p>
      </div>

      <div className="settings-section">
        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar">
              <span>{influencer?.name?.charAt(0)?.toUpperCase() || 'I'}</span>
            </div>
            <div className="profile-info">
              <h3>{influencer?.name || 'Influencer'}</h3>
              <p className="profile-email">{influencer?.email || 'No configurado'}</p>
              <div className="profile-status">
                <span className="status-dot active"></span>
                <span>Cuenta Activa</span>
              </div>
            </div>
          </div>

          <div className="profile-details">
            <div className="detail-card">
              <div className="detail-icon">🏷️</div>
              <div className="detail-content">
                <label>Código de Influencer</label>
                <p>{influencer?.couponCode || 'N/A'}</p>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-icon">📧</div>
              <div className="detail-content">
                <label>Email</label>
                <p>{influencer?.email || 'No configurado'}</p>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-icon">💼</div>
              <div className="detail-content">
                <label>Tasa de Comisión</label>
                <p>{(influencer?.commissionRate * 100).toFixed(1)}%</p>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-icon">📅</div>
              <div className="detail-content">
                <label>Miembro desde</label>
                <p>{influencer?.createdAt ? (() => {
                  try {
                    const date = new Date(influencer.createdAt);
                    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('es-ES', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    });
                  } catch (error) {
                    console.error('Error formateando fecha:', error);
                    return 'N/A';
                  }
                })() : 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
